import { useDataModel } from '@thd-nucleus/data-sources';

import React, { createContext, useMemo, useState, useEffect } from 'react';
import { paymentCards } from '../models/graphQLModels';
import { IPayment } from '../models/payments';
import { getChannelID, redirectToSignIn } from '../utils/general';
import { IPhone } from '../models/address';
import { useInstantCheckoutStatus } from '../hooks/use-instant-checkout-status';
import { TAlertStatus, TAlertType } from '../models/common/alert';
import {
  ERR_GENERIC,
  ERR_REDIRECT,
} from '../constants/response-error-messages';
import { getIsAuthError } from '../constants/error-codes';
import { createNewRelicActions, logNewRelicAction } from '../utils/newrelic';
import { getAllErrorCodes } from '../utils/errorUtils';

export interface IPaymentsContextValues {
  payments: IPayment[];
  hasPayments: () => boolean;

  selectedPaymentMethod: IPayment | undefined;
  setSelectedPaymentMethod: React.Dispatch<
    React.SetStateAction<IPayment | undefined>
  >;
  refetchPayments: () => void;

  paymentsLoading: boolean;
  defaultPayment: IPayment | undefined;
}

export const PaymentsContext = createContext<IPaymentsContextValues | null>(
  null,
);

export const PaymentsContextProvider = ({
  children,
  userId,
  customerAccountId,
}: {
  children: React.ReactNode;
  userId: string;
  customerAccountId: string;
}) => {
  const channelId = getChannelID();
  const actions = createNewRelicActions('PaymentsContextProvider');

  const { error, data, loading, refetch } = useDataModel('paymentCards', {
    variables: {
      userId,
      customerAccountId,
      channelId,
    },
    skip: !userId || !customerAccountId,
    ssr: false,
    context: { withAuth: true },
    notifyOnNetworkStatusChange: true,
  });

  const { setAlertMessage } = useInstantCheckoutStatus();

  const payments: IPayment[] = useMemo(() => {
    return data?.paymentCards?.paymentCards
      ?.filter((payment: IPayment) => payment.type === 'CREDIT_CARD')
      .map((payment: IPayment): IPayment => {
        return {
          isDefault: payment?.isDefault,
          id: payment?.id,
          isInstantCheckoutDefault: payment?.isInstantCheckoutDefault,
          nickName: payment?.nickName,
          billingAddress: {
            id: payment.billingAddress?.id,
            isDefault: payment.billingAddress?.isDefault,
            lastModifiedDate: payment.billingAddress?.lastModifiedDate,
            isInstantCheckoutDefault:
              payment?.billingAddress?.isInstantCheckoutDefault,
            nickName: payment?.billingAddress?.nickName,
            name: {
              firstName: payment?.billingAddress?.name?.firstName,
              lastName: payment?.billingAddress?.name?.lastName,
            },
            phones: payment?.billingAddress?.phones?.map((phone): IPhone => {
              return {
                number: phone?.number,
                id: phone?.id,
                isPrimary: phone?.isPrimary ?? false,
              };
            }),
            postalAddress: {
              addressLine1:
                payment?.billingAddress?.postalAddress?.addressLine1,
              addressLine2:
                payment?.billingAddress?.postalAddress?.addressLine2,
              city: payment?.billingAddress?.postalAddress?.city,
              country: payment?.billingAddress?.postalAddress?.country,
              stateProvince:
                payment?.billingAddress?.postalAddress?.stateProvince,
              zipCode: payment?.billingAddress?.postalAddress?.zipCode,
            },
          },
          cardDetails: {
            cardBrand: {
              name: payment?.cardDetails?.cardBrand?.name,
              displayName: payment?.cardDetails?.cardBrand?.displayName,
            },
            cardHolderName: payment?.cardDetails?.cardHolderName,
            cardNumberLast4: payment?.cardDetails?.cardNumberLast4,
            expirationMonth: payment?.cardDetails?.expirationMonth,
            expirationYear: payment?.cardDetails?.expirationYear,
            isExpired: payment?.cardDetails?.isExpired,
          },
        };
      });
  }, [data]);

  useEffect(() => {
    if (error) {
      if (!getAllErrorCodes(error)?.includes('PYMTCRD_ERR_113')) {
        logNewRelicAction(actions.PAYMENT, error);
      }

      if (getIsAuthError(error)) {
        setAlertMessage({
          alertType: TAlertType.GENERAL,
          alertStatus: TAlertStatus.INFO,
          text: ERR_REDIRECT.message,
        });
        redirectToSignIn();
      } else if (!getAllErrorCodes(error)?.includes('PYMTCRD_ERR_113')) {
        setAlertMessage({
          alertType: TAlertType.GENERAL,
          alertStatus: TAlertStatus.ERROR,
          text: ERR_GENERIC.message,
        });
      }
    } else if (data) {
      logNewRelicAction(actions.PAYMENT);
    }
  }, [actions.PAYMENT, data, error, setAlertMessage]);

  const hasPayments = (): boolean => payments?.length > 0;

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<
    IPayment | undefined
  >(payments?.find((payment) => payment.isInstantCheckoutDefault));

  return (
    <PaymentsContext.Provider
      value={{
        payments,
        hasPayments,

        selectedPaymentMethod,
        setSelectedPaymentMethod,
        refetchPayments: refetch,

        paymentsLoading: loading,
        defaultPayment: payments?.find(
          (payment) => payment.isInstantCheckoutDefault,
        ),
      }}
    >
      {children}
    </PaymentsContext.Provider>
  );
};

PaymentsContextProvider.dataModel = {
  paymentCards,
};
